/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React from 'react'
import PropTypes from 'prop-types'
import {
    Button,
    ButtonGroup,
    Divider,
    Stack,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {defineMessage, FormattedMessage} from 'react-intl'
import {useItemVariant} from '@salesforce/retail-react-app/app/components/item-variant'
import {noop} from '@salesforce/retail-react-app/app/utils/utils'
import withRegistration from '../../../components/with-registration'
import Link from '../../../components/link'
import {getCartLineUnitPrice, getProductLineItemUrl} from '../../../utils/product-utils'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import {useSiteContext} from '../../../contexts/site'
import {useGlobaleContext, useResolvedCountryISO} from '../../../contexts/globale'
import useTagManager from '../../../hooks/use-tag-manager'
import {scheduleAfterPaint} from '../../../utils/common-utils'

export const REMOVE_CART_ITEM_CONFIRMATION_DIALOG_CONFIG = {
    dialogTitle: defineMessage({
        defaultMessage: 'Confirm Remove Item',
        id: 'confirmation_modal.remove_cart_item.title.confirm_remove'
    }),
    confirmationMessage: defineMessage({
        defaultMessage: 'Are you sure you want to remove this item from your cart?',
        id: 'confirmation_modal.remove_cart_item.message.sure_to_remove'
    }),
    primaryActionLabel: defineMessage({
        defaultMessage: 'Yes, remove item',
        id: 'confirmation_modal.remove_cart_item.action.yes'
    }),
    primaryActionAriaLabel: defineMessage({
        defaultMessage: 'Yes, remove item',
        id: 'confirmation_modal.remove_cart_item.assistive_msg.yes'
    }),
    alternateActionLabel: defineMessage({
        defaultMessage: 'No, keep item',
        id: 'confirmation_modal.remove_cart_item.action.no'
    }),
    alternateActionAriaLabel: defineMessage({
        defaultMessage: 'No, keep item',
        id: 'confirmation_modal.remove_cart_item.assistive_msg.no'
    }),
    onPrimaryAction: noop
}

const ButtonWithRegistration = withRegistration(Button)

/**
 * Renders secondary actions on a product-item card in the form of a button group.
 * Represents other actions you want the user to perform with the product-item
 * (eg.: Remove or Edit or Add to wishlist for cart items)
 */
const CartSecondaryButtonGroup = ({onAddToWishlistClick = noop, onEditClick = noop}) => {
    const variant = useItemVariant()
    const tagManager = useTagManager()
    const {countryISO, globaleCurrency} = useGlobaleContext()
    const resolvedCountryISO = useResolvedCountryISO()
    const {data: basket} = useCurrentBasket()
    const cartStyles = useMultiStyleConfig('CartStyles')
    const isBonusProduct = variant?.bonusProductLineItem
    const isColorBarProduct =
        variant?.c_fluidConfigureImageURL &&
        variant?.c_fluidConfigureRecipeID &&
        variant?.c_fluidConfigureProductID
    const renderEditButton =
        isColorBarProduct || (variant.id && !variant.type?.item) || isBonusProduct
    const renderWishlistButton = !variant?.c_fluidConfigureImageURL
    const showWishlistButton =
        renderWishlistButton && !isBonusProduct && resolvedCountryISO === 'US'
    const productUrlFormatted = getProductLineItemUrl(basket, variant)
    const hasEngraving = variant?.c_driveRecipeID

    const getLinkProps = (enabled, isExternal) =>
        enabled
            ? {
                  to: productUrlFormatted?.to || productUrlFormatted?.href,
                  as: Link,
                  isExternal
              }
            : {}

    const colorBarProps = getLinkProps(isColorBarProduct, true)
    const engravingProps = getLinkProps(hasEngraving, false)
    const {setOpenBonusModal, setEditBonusItemInCart, setForceOpenGwpDrawer} = useSiteContext()
    return (
        (renderEditButton || renderWishlistButton) && (
            <>
                <Stack
                    direction={{base: 'column', lg: 'row'}}
                    alignItems={{base: 'flex-start', lg: 'center'}}
                    justifyContent={{base: 'flex-start', lg: 'space-between'}}
                    divider={<Divider display={{base: 'block', lg: 'none'}} />}
                    width="fit-content"
                    className="seccondary-button-wrapper"
                >
                    <ButtonGroup gap="20px" className="button-group">
                        {renderEditButton && (
                            <Button
                                variant="link"
                                onClick={() => {
                                    if (isBonusProduct) {
                                        scheduleAfterPaint(() => {
                                            setOpenBonusModal(true)
                                            setEditBonusItemInCart(true)
                                            setForceOpenGwpDrawer(true)
                                        })
                                    }
                                    if (!isColorBarProduct && !hasEngraving && !isBonusProduct) {
                                        window.listId = 'cart'
                                        onEditClick(variant)
                                    }
                                }}
                                {...colorBarProps}
                                {...engravingProps}
                                {...cartStyles.lineItems.item.secondaryButtons}
                                className="edit-button"
                            >
                                <FormattedMessage
                                    defaultMessage="Edit"
                                    id="cart_secondary_button_group.action.edit"
                                />
                            </Button>
                        )}

                        {showWishlistButton && (
                            <ButtonWithRegistration
                                key="wishlist-button"
                                onClick={async (e) => {
                                    const quantity = variant.quantity || 1
                                    const unitPrice = getCartLineUnitPrice(variant)
                                    let indexPosition

                                    try {
                                        const cartDiv = e.target?.closest('.sf-cart-item')
                                        const cartDivs = document.querySelectorAll('.sf-cart-item')
                                        const uuid = cartDiv?.getAttribute('data-uuid')
                                        // NOTE: Index is resolved from the DOM because hidden Color Bar
                                        // metals/stones affect the visible cart order.
                                        const index = [...cartDivs].findIndex(
                                            (el) => el.dataset.uuid === uuid
                                        )
                                        indexPosition = index >= 0 ? index + 1 : undefined
                                    } catch {
                                        indexPosition = undefined
                                    }

                                    // removeFromCart runs inside handleAddToWishlist; use the same unit price.
                                    window.updatedItemPrice = unitPrice
                                    window.updatedIndex = indexPosition

                                    const status = await onAddToWishlistClick(variant)
                                    if (!status) {
                                        delete window.updatedItemPrice
                                        delete window.updatedIndex
                                        return
                                    }

                                    tagManager.trackGA4Event({
                                        eventName: 'add_to_wishlist',
                                        itemsAdded: [
                                            {
                                                product: variant,
                                                custom: {
                                                    price: unitPrice,
                                                    quantity
                                                }
                                            }
                                        ],
                                        method: 'cart',
                                        currency: globaleCurrency || 'USD',
                                        listName: 'cart',
                                        listId: 'cart',
                                        indexPosition
                                    })
                                }}
                                productid={variant?.id}
                                productname={variant?.name}
                                category={variant?.c_primaryCategory || ''}
                                ispdp={false}
                                isCartItem={true}
                                variant="unstyled"
                                alignItems="center"
                                justifyContent="center"
                                aria-label="Wishlist"
                                {...cartStyles.lineItems.item.secondaryButtons}
                                visibility={countryISO ? 'visible' : 'hidden'}
                                pointerEvents={countryISO ? 'auto' : 'none'}
                            >
                                <FormattedMessage
                                    defaultMessage="Save to Wishlist"
                                    id="cart_secondary_button_group.action.added_to_wishlist"
                                />
                            </ButtonWithRegistration>
                        )}
                    </ButtonGroup>
                </Stack>
            </>
        )
    )
}

CartSecondaryButtonGroup.propTypes = {
    onClick: PropTypes.func,
    onEditClick: PropTypes.func,
    onAddToWishlistClick: PropTypes.func,
    onRemoveItemClick: PropTypes.func,
    onIsAGiftChange: PropTypes.func,
    isAGift: PropTypes.bool
}

export default CartSecondaryButtonGroup
